import { z } from 'zod';
/**
 * Card condition grades, in descending quality order.
 * Used across pricing tiers and trade-in valuation.
 */
export const CARD_CONDITIONS = ['NM', 'LP', 'MP', 'HP', 'DMG'];
export const CARD_PRINTINGS = ['Normal', 'Foil', 'Reverse', 'Holo', 'FirstEdition'];
export const CARD_LANGUAGES = ['EN', 'JP', 'DE', 'FR', 'IT', 'ES', 'PT', 'KO', 'CN'];
/** Third-party grading companies for slabbed cards. */
export const CARD_GRADING_COMPANIES = ['psa', 'cgc', 'beckett', 'tag', 'sgc', 'other'];
export const USER_ROLES = ['owner', 'manager', 'clerk', 'buyer'];
export const PRICE_SOURCES = [
    'tcgapi_market',
    'tcgapi_low',
    'tcgapi_median',
    'tcgapi_buylist',
    'pkmnprices_market',
    'pkmnprices_low',
    'pkmnprices_cardmarket',
    'pkmnprices_graded_ebay',
    'manual_override',
];
export const ORDER_STATUSES = [
    'open',
    'pending_payment',
    'paid',
    'voided',
    'refunded',
    'partially_refunded',
];
export const TRADE_STATUSES = [
    'draft',
    'pending_approval',
    'approved',
    'rejected',
    'completed',
];
export const PAYOUT_KINDS = ['cash', 'store_credit'];
/** Allowed POS provider literal. Clover is the exclusive payment processor. */
export const POS_PROVIDERS = ['clover'];
export const GAMES = [
    'mtg',
    'pokemon',
    'yugioh',
    'lorcana',
    'one_piece',
    'flesh_and_blood',
    'sealed',
    'supplies',
    'other',
];
// ---------- Request / response DTOs ----------
export const ScanRequest = z.object({
    barcode: z.string().min(3).max(128),
    orderId: z.string().uuid().optional(),
    registerId: z.string().min(1).optional(),
});
export const ScanResponse = z.object({
    skuId: z.string().uuid(),
    productId: z.string().uuid(),
    name: z.string(),
    setName: z.string().nullable(),
    cardNumber: z.string().nullable(),
    condition: z.enum(CARD_CONDITIONS).nullable(),
    printing: z.enum(CARD_PRINTINGS),
    language: z.enum(CARD_LANGUAGES),
    gradingCompany: z.enum(CARD_GRADING_COMPANIES).nullable(),
    grade: z.string().nullable(),
    certNumber: z.string().nullable(),
    imageUrl: z.string().url().nullable(),
    priceCents: z.number().int().nonnegative(),
    stockOnHand: z.number().int().nonnegative(),
    stockReserved: z.number().int().nonnegative(),
});
export const CreateOrderRequest = z.object({
    locationId: z.string().uuid(),
    registerId: z.string().min(1).optional(),
    customerId: z.string().uuid().optional(),
});
export const CheckoutRequest = z.object({
    provider: z.enum(POS_PROVIDERS).default('clover'),
    deviceId: z.string().min(1),
    tipCents: z.number().int().nonnegative().optional(),
});
export const AddItemRequest = z.object({
    barcode: z.string().min(1),
    clientRequestId: z.string().uuid().optional(),
});
export const LoginRequest = z.object({
    email: z.string().email(),
    password: z.string().min(8).max(128),
});
export const SignupRequest = z.object({
    storeName: z.string().min(2).max(120),
    ownerEmail: z.string().email(),
    ownerPassword: z.string().min(8).max(128),
    ownerName: z.string().min(1).max(120),
    timezone: z.string().min(1).max(64).optional(),
    /** Name of the first location (defaults to "Main" if omitted). */
    locationName: z.string().min(1).max(120).optional(),
});
/** Tenant details used after Firebase creates and authenticates an owner. */
export const FirebaseProvisionRequest = z.object({
    storeName: z.string().min(2).max(120),
    ownerName: z.string().min(1).max(120),
    timezone: z.string().min(1).max(64).optional(),
    locationName: z.string().min(1).max(120).optional(),
});
export const ForgotPasswordRequest = z.object({
    email: z.string().email(),
});
export const ResetPasswordRequest = z.object({
    token: z.string().min(16).max(256),
    password: z.string().min(8).max(128),
});
export const RefreshRequest = z.object({
    refreshToken: z.string().min(16).max(512).optional(),
});
export const LogoutRequest = z.object({
    refreshToken: z.string().min(16).max(512).optional(),
});
export const LocationSummary = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
export const CreateLocationRequest = z.object({
    name: z.string().min(1).max(120),
});
export const AuthTokens = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int().positive(),
    user: z.object({
        id: z.string().uuid(),
        storeId: z.string().uuid(),
        email: z.string().email(),
        role: z.enum(USER_ROLES),
        displayName: z.string(),
    }),
});
export const TradeItemInput = z.object({
    skuId: z.string().uuid().optional(),
    // If no SKU yet (brand-new card to the store) accept lookup hints:
    tcgapiProductId: z.string().min(1).optional(),
    game: z.enum(GAMES).optional(),
    name: z.string().min(1).optional(),
    imageSourceUrl: z.string().url().nullable().optional(),
    rarity: z.string().min(1).nullable().optional(),
    condition: z.enum(CARD_CONDITIONS),
    printing: z.enum(CARD_PRINTINGS),
    language: z.enum(CARD_LANGUAGES).default('EN'),
    // Graded-slab fields. When gradingCompany is present the SKU is stored as a
    // graded line (its in-house condition is dropped) and priced off sold comps.
    gradingCompany: z.enum(CARD_GRADING_COMPANIES).optional(),
    grade: z.string().min(1).max(8).optional(),
    certNumber: z.string().min(1).max(64).optional(),
    quantity: z.number().int().positive(),
    // Percentage modifier applied after the base payout multiplier.
    payoutModifierPercent: z.number().finite().optional(),
    // Overrides for the suggested trade value (cents per unit):
    overrideValueCents: z.number().int().nonnegative().optional(),
    // Optional market signal captured at intake time for current price upsert.
    marketPriceCents: z.number().int().nonnegative().nullable().optional(),
});
export const CreateTradeRequest = z.object({
    locationId: z.string().uuid(),
    customerId: z.string().uuid().optional(),
    payout: z.enum(PAYOUT_KINDS),
    items: z.array(TradeItemInput).min(1),
});
export const SOCKET_EVENTS = {
    cartItemAdded: 'cart.itemAdded',
    cartItemRemoved: 'cart.itemRemoved',
    cartTotals: 'cart.totals',
    inventoryUpdated: 'inventory.updated',
    orderCompleted: 'order.completed',
    orderRefunded: 'order.refunded',
    tradeCreated: 'tradein.created',
    tradeApproved: 'tradein.approved',
};
// ---------- Helpers ----------
/**
 * Deterministically hash a SKU identity for de-duplication. Keyed on the local
 * `products.id` (provider-agnostic) plus the variant dimensions. Graded slabs
 * add grading company + grade and carry a null condition; raw cards leave the
 * grading fields empty.
 */
export function skuIdentityKey(args) {
    return [
        args.productId,
        args.condition ?? '',
        args.printing,
        args.language,
        args.gradingCompany ?? '',
        args.grade ?? '',
    ].join('|');
}
// ---------- API response shapes shared with the web client ----------
export * from './dto.js';
// ---------- Text normalization ----------
export * from './text.js';
//# sourceMappingURL=index.js.map